"use client";

import { router } from "@inertiajs/react";
import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import { CompanyFilterSelect } from "@/components/company-filter-select";
import { DataTablePagination } from "@/components/data-table-pagination";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import {
  InputGroup,
  InputGroupInput,
} from "@/components/ui/input-group";
import { useDataTable } from "@/hooks/use-data-table";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import { useSyncedDebouncedValue } from "@/hooks/use-synced-debounced-value";
import { DataTable } from "@/lib/data-table";
import type { Company, PagyMetadata, RansackFilters, Sector } from "@/types";

import { columns } from "./columns";

interface SectorsDataTableProps {
  sectors: Sector[];
  pagy: PagyMetadata;
  filters: RansackFilters;
  companies: Company[];
}

export function SectorsDataTable({
  sectors,
  pagy,
  filters,
  companies,
}: SectorsDataTableProps) {
  const routes = useOrganizationRoutes();
  const {
    handleFilterChange,
    isLoading,
    handlePageChange,
    handleSortingChange,
    getSortingState,
  } = useDataTable({ filters, pagy, path: routes.sectors.index() });

  const {
    value: searchValue,
    setValue: setSearchValue,
    debouncedValue: debouncedSearchValue,
  } = useSyncedDebouncedValue(filters.name_or_code_cont ?? "");
  const [companyFilter, setCompanyFilter] = useState(
    filters.company_id_eq ?? "",
  );

  useEffect(() => {
    setCompanyFilter(filters.company_id_eq ?? "");
  }, [filters.company_id_eq]);

  useEffect(() => {
    if ((filters.name_or_code_cont ?? "") !== debouncedSearchValue) {
      handleFilterChange({
        name_or_code_cont: debouncedSearchValue
          ? debouncedSearchValue
          : undefined,
      });
    }
    // Only react to debounced local input, to avoid loops when filters change from server.
  }, [debouncedSearchValue]);

  const handleCompanyChange = (value: string) => {
    const companyId = value === "all" ? undefined : value;
    setCompanyFilter(value === "all" ? "" : value);
    handleFilterChange({ company_id_eq: companyId });
  };

  const handleReset = () => {
    setSearchValue("");
    setCompanyFilter("");
    router.get(
      window.location.pathname,
      {},
      {
        preserveState: true,
        preserveScroll: true,
        only: ["data", "filters"],
        reset: ["data"],
      },
    );
  };

  const hasActiveFilters = Boolean(searchValue || companyFilter);

  const renderExternalToolbar = () => (
    <div className="flex flex-wrap items-center gap-3">
      <ButtonGroup>
        <InputGroup className="w-[300px]">
          <InputGroupInput
            placeholder="Buscar por nombre o código"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
          />
        </InputGroup>
        <CompanyFilterSelect
          value={companyFilter || "all"}
          onChange={handleCompanyChange}
          companies={companies}
          className="w-[220px] rounded-l-none border-l-0"
        />
      </ButtonGroup>

      {hasActiveFilters ? (
        <Button
          variant="outline"
          onClick={handleReset}
          className="h-9 px-2 lg:px-3"
        >
          <Trash2 className="h-4 w-4" /> Limpiar filtros
        </Button>
      ) : null}
    </div>
  );

  return (
    <div className="space-y-4">
      {renderExternalToolbar()}

      <div className={isLoading ? "opacity-50" : ""}>
        <DataTable
          columns={columns(routes)}
          data={sectors}
          sorting={getSortingState()}
          onSortingChange={handleSortingChange}
          manualSorting={true}
          manualPagination={true}
          pageCount={pagy.pages}
          pagination={false}
        />
      </div>

      <DataTablePagination
        pagy={pagy}
        isLoading={isLoading}
        onPageChange={handlePageChange}
        entityName="sectores"
      />
    </div>
  );
}
